import { EmbedTypeV3 } from '@opengov/reporting-types';

export type IVisualizationSelectOptionV3Embeds = {
  value: string;
  icon: string;
  name: string;
  isCapitalIcon: boolean;
};

export const vizOptionsChartsDetails = [
  { name: 'Bar Chart', value: EmbedTypeV3.BarChart, icon: 'graphtype-bar', isCapitalIcon: true },
  {
    name: 'Horizontal Bar Chart',
    value: EmbedTypeV3.HorizontalBarChart,
    icon: 'graphtype-horizontalbar',
    isCapitalIcon: true
  },
  { name: 'Grouped Bar Chart', value: EmbedTypeV3.GroupedBarChart, icon: 'graphtype-bar', isCapitalIcon: true },
  {
    name: 'Stacked Bar Chart',
    value: EmbedTypeV3.StackedBarChart,
    icon: 'icon-graphtype-percentage-bar',
    isCapitalIcon: false
  },
  {
    name: 'Stacked Horizontal Bar Chart',
    value: EmbedTypeV3.StackedHorizontalBarChart,
    icon: 'graphtype-horizontalbar',
    isCapitalIcon: true
  },
  { name: 'Line Chart', value: EmbedTypeV3.LineChart, icon: 'graphtype-line', isCapitalIcon: true },
  { name: 'Pie Chart', value: EmbedTypeV3.PieChart, icon: 'icon-graphtype-pie', isCapitalIcon: false }
];

export const vizOptionsMds = [
  ...vizOptionsChartsDetails,
  { name: 'Gauge', value: EmbedTypeV3.Gauge, icon: 'graphtype-gauge', isCapitalIcon: true }
];

export type IVisualizationSelectOption = {
  chartType: string;
  icon: string;
  name: string;
  tileType: string;
  isCapitalIcon: boolean;
};

export const unfilteredVisualizationSelectData: IVisualizationSelectOption[] = [
  // the content of unfilteredVisualizationSelectData will be updated
  // after the embed services being updated
  // chartType is an abstraction of charts, e.g. line, bar, stackedbar, etc.
  // tileType refers to specific implementation of chartTypes
  // The line_chart in embed service v1 tiles is an example of tileType
  // So is transparency_tacked_bar in embed service v2
  {
    icon: 'graphtype-gauge',
    chartType: 'gauge',
    tileType: 'tile_gauge',
    name: 'Gauge',
    isCapitalIcon: true
  },
  {
    icon: 'graphtype-line',
    chartType: 'line',
    tileType: 'tile_line',
    name: 'Line Chart',
    isCapitalIcon: true
  },
  {
    icon: 'graphtype-line',
    chartType: 'line_grid',
    tileType: 'tile_line_grid',
    name: 'Line Chart',
    isCapitalIcon: true
  },
  {
    icon: 'graphtype-bar',
    chartType: 'split_bar',
    tileType: 'tile_split_bar',
    name: 'Bar',
    isCapitalIcon: true
  },
  {
    icon: 'graphtype-bar',
    chartType: 'bar_grid',
    tileType: 'tile_bar_grid',
    name: 'Bar',
    isCapitalIcon: true
  },
  {
    icon: 'graphtype-bar',
    chartType: 'bar',
    tileType: 'tile_bar',
    name: 'Bar',
    isCapitalIcon: true
  },
  {
    icon: 'graphtype-bullet',
    chartType: 'bullet_bar',
    tileType: 'tile_bullet_bar',
    name: 'Actuals vs Budget',
    isCapitalIcon: true
  },
  {
    icon: 'graphtype-percentBar',
    chartType: 'percent_bullet_bar',
    tileType: 'tile_percent_bullet_bar',
    name: 'Actuals as % Budget',
    isCapitalIcon: true
  },
  {
    icon: 'icon-graphtype-percentage-bar',
    chartType: 'transparency_stacked_bar',
    tileType: 'transparency_stacked_bar',
    name: 'Stacked Bar Chart',
    isCapitalIcon: false
  },
  {
    icon: 'icon-graphtype-pie',
    chartType: 'transparency_pie',
    tileType: 'transparency_pie',
    name: 'Pie Chart',
    isCapitalIcon: false
  },
  {
    icon: 'icon-table-1',
    chartType: 'transparency_table',
    tileType: 'transparency_table',
    name: 'Table Chart',
    isCapitalIcon: false
  }
];

// Used to get corresponding chartTypes for v1 types (safer than using str.replace('tile_',''))
export const CHART_TYPES = {
  tile_gauge: 'gauge',
  tile_line: 'line',
  tile_line_grid: 'line_grid',
  tile_split_bar: 'split_bar',
  tile_bar_grid: 'bar_grid',
  tile_bar: 'bar',
  tile_bullet_bar: 'bullet_bar',
  tile_percent_bullet_bar: 'percent_bullet_bar',
  tile_transparency_stacked_bar: 'transparency_stacked_bar',
  tile_transparency_pie: 'transparency_pie',
  tile_transparency_table: 'transparency_table'
};

const filterVisualizations = (visualizations: IVisualizationSelectOption[], tileTypes: string[]) =>
  visualizations.filter((item) => tileTypes.indexOf(item.tileType) !== -1);

const identifyDefaultVisType = (visualizations: IVisualizationSelectOption[], defaultTileType: string) => {
  let defaultVisIdx: number;
  let defaultVisName: string;

  for (let i = 0; i < visualizations.length; i++) {
    if (visualizations[i].tileType === defaultTileType) {
      defaultVisIdx = i;
      defaultVisName = visualizations[i].name;
    }
  }

  if (defaultVisIdx !== undefined) {
    if (!visualizations[defaultVisIdx].name.includes('(recommended)')) {
      visualizations[defaultVisIdx].name = `${defaultVisName} (recommended)`;
    }
  }

  return visualizations;
};

export const buildVisualizationSelectData = (
  defaultTileType: string,
  targetTileTypes: string[]
): IVisualizationSelectOption[] => {
  let visualizationSelectDataRes: IVisualizationSelectOption[] = [];
  if (targetTileTypes && defaultTileType) {
    const filteredVisualizationSelectDataRes = filterVisualizations(unfilteredVisualizationSelectData, targetTileTypes);
    visualizationSelectDataRes = filteredVisualizationSelectDataRes;
    visualizationSelectDataRes = identifyDefaultVisType(filteredVisualizationSelectDataRes, defaultTileType);

    return visualizationSelectDataRes;
  }
};

export const getVisualizationSelectedValue = (selectedType: string): string => {
  let selectedValue = '';
  const selectedOption = unfilteredVisualizationSelectData.filter(
    (option) => option.tileType === selectedType || option.chartType === selectedType
  )[0] || {
    name: ''
  };
  selectedValue = selectedOption.name;
  return selectedValue;
};
